import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Car,
  Home as HomeIcon,
  Building2,
  Plane,
  Users,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Phone,
} from "lucide-react";
import { services, company } from "../mock";
import QuoteForm from "../components/QuoteForm";

const iconMap = { Car, Home: HomeIcon, Building2, Plane, Users, ShieldCheck };

const ServicePage = ({ slug }) => {
  const service = services.find((s) => s.slug === slug);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  if (!service) return null;
  const Icon = iconMap[service.icon] || ShieldCheck;

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="aurora bg-[#8ec4ec] h-80 w-80 -top-20 -left-16" />
        <div className="aurora bg-[#8ec4ec] h-80 w-80 top-0 right-0" />
        <div className="max-w-7xl mx-auto px-6 pt-14 pb-16 grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div className="fade-up">
            <nav className="text-sm text-slate-500 mb-5 flex items-center gap-2">
              <Link to="/" className="hover:text-[#0066b3]">
                Home
              </Link>
              <span>/</span>
              <span className="text-[#0066b3] font-medium">{service.title}</span>
            </nav>
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#0066b3] to-[#003c7a] flex items-center justify-center text-white mb-5 shadow-lg">
              <Icon size={28} />
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold leading-tight text-[#0a2b52]">
              {service.hero}
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              {service.intro}
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="brand-btn px-6 py-3.5 rounded-full font-semibold flex items-center gap-2"
              >
                Get a Quote <ArrowRight size={18} />
              </Link>
              <a
                href={`tel:${company.phoneRaw}`}
                className="glass-strong px-6 py-3.5 rounded-full font-semibold text-[#0a2b52] flex items-center gap-2 hover:text-[#0066b3] transition-colors"
              >
                <Phone size={18} /> {company.phone}
              </a>
            </div>
          </div>
          <div className="fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="glass-strong rounded-3xl p-2">
              <img
                src={service.image}
                alt={service.title}
                className="rounded-2xl w-full h-[360px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key points */}
      <section className="max-w-7xl mx-auto px-6 pt-10">
        <div className="glass-strong rounded-3xl p-8 md:p-10">
          <h2 className="font-display text-2xl font-bold text-[#0a2b52] mb-6">
            What's included
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {service.points.map((p) => (
              <div key={p} className="flex items-start gap-3">
                <CheckCircle2 size={22} className="text-[#0066b3] shrink-0 mt-0.5" />
                <span className="text-slate-700">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sub services */}
      <section className="max-w-7xl mx-auto px-6 pt-16">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-[#0066b3] font-semibold text-sm tracking-wider uppercase">
            Coverage Options
          </span>
          <h2 className="font-display text-3xl font-extrabold text-[#0a2b52] mt-2">
            Explore our {service.title.toLowerCase()} options
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {service.subServices.map((sub, i) => {
            const CardTag = sub.to ? Link : "div";
            const cardProps = sub.to ? { to: sub.to } : {};
            return (
              <CardTag
                key={sub.title}
                {...cardProps}
                className={`group glass rounded-3xl p-7 card-hover fade-up ${sub.to ? "block cursor-pointer" : ""}`}
                style={{ animationDelay: `${i * 0.06}s` }}
              >
                <div className="h-11 w-11 rounded-xl bg-[#0066b3]/10 flex items-center justify-center text-[#0066b3] font-display font-bold text-lg mb-4">
                  {i + 1}
                </div>
                <h3 className="font-display text-lg font-bold text-[#0a2b52]">
                  {sub.title}
                </h3>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">
                  {sub.desc}
                </p>
                {sub.to && (
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[#0066b3] font-semibold text-sm group-hover:gap-3 transition-all">
                    Learn more <ArrowRight size={16} />
                  </span>
                )}
              </CardTag>
            );
          })}
        </div>
      </section>

      {/* Quote form */}
      <section className="max-w-7xl mx-auto px-6 pt-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-[#0a2b52]">
              Ready to get covered?
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Request a free {service.title.toLowerCase()} quote below and our
              Surrey-based agents will help you find the perfect coverage at the
              best rate. No obligation.
            </p>
            <div className="mt-8 glass rounded-2xl p-6">
              <div className="flex items-center gap-3 text-[#0a2b52]">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#0066b3] to-[#003c7a] flex items-center justify-center text-white">
                  <Phone size={20} />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Call us directly</div>
                  <a
                    href={`tel:${company.phoneRaw}`}
                    className="font-display font-bold text-lg text-[#0a2b52] hover:text-[#0066b3]"
                  >
                    {company.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="glass-strong rounded-3xl p-7 md:p-9">
            <h3 className="font-display text-2xl font-bold text-[#0a2b52] mb-6">
              Free Quote Request
            </h3>
            <QuoteForm defaultType={service.title} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
